import { useEffect, useMemo, useState } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";

import MenuCameriereView from "./MenuCameriere.view";
import { useMenuCameriereLogic } from "./MenuCameriere.logic";

import "./MenuCameriere.css";

export default function MenuCameriere() {
  /* =========================
     PARAMETRI URL / STORAGE
  ========================= */
  const params = useMemo(() => new URLSearchParams(window.location.search), []);

  const rawLocale = params.get("locale") || localStorage.getItem("localeTag");
  const localeTag = rawLocale
    ?.normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  const tavolo = params.get("tavolo") || localStorage.getItem("tavoloNumero");
  const numeroCoperti = Number(params.get("numeroCoperti")) || 1;
  const cameriere = params.get("cameriere") || "";

  /* =========================
     STATE
  ========================= */
  const [menu, setMenu] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [ordiniTavolo, setOrdiniTavolo] = useState([]);
  const [loading, setLoading] = useState(true);

  /* =========================
     CARICAMENTO MENU
  ========================= */
  useEffect(() => {
    if (!localeTag) return;

    const q = query(
      collection(db, "menu"),
      where("localeTag", "==", localeTag)
    );

    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.ordine ?? 0) - (b.ordine ?? 0));
      setMenu(list);
    });

    return () => unsub();
  }, [localeTag]);

  useEffect(() => {
    if (!localeTag) return;

    const q = query(
      collection(db, "categorie"),
      where("localeTag", "==", localeTag)
    );

    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((c) => c.visibile !== false)
        .sort((a, b) => (a.ordine ?? 0) - (b.ordine ?? 0));

      setCategorie(list);

      if (list.length > 0) {
        logic.setCategoriaSelezionata(list[0].nome);
      }
    });

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localeTag]);

  /* =========================
     CARICAMENTO ORDINI TAVOLO (COME PRIMA)
     - mostra tutti gli ordini non finali
  ========================= */
  useEffect(() => {
    if (!localeTag || !tavolo) {
      setOrdiniTavolo([]);
      setLoading(false);
      return;
    }

    // NB: Firestore consente "not-in" max 10 valori.
    const q = query(
      collection(db, "ordini"),
      where("locale", "==", localeTag),
      where("table", "==", tavolo),
      where("stato", "not-in", ["pagamento", "archiviato"])
    );

    const unsub = onSnapshot(q, (snap) => {
      const ordini = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Ordina dal più recente al più vecchio (timestamp se esiste)
      ordini.sort((a, b) => {
        const ta = a.timestamp?.seconds ?? 0;
        const tb = b.timestamp?.seconds ?? 0;
        return tb - ta;
      });

      setOrdiniTavolo(ordini);
      setLoading(false);
    });

    return () => unsub();
  }, [localeTag, tavolo]);

  /* =========================
     LOGIC HOOK (solo carrello + invio)
  ========================= */
  const logic = useMenuCameriereLogic({ menu });

  /* =========================
     INVIO NUOVO ORDINE (SEMPRE addDoc)
  ========================= */
  const handleInviaOrdine = async () => {
    const ok = await logic.inviaOrdine({
      localeTag,
      tavolo,
      numeroCoperti,
      cameriere
    });

    // Se vuoi restare nella pagina (consigliato in PWA), non redirectare.
    // Se invece vuoi tornare alla Dashboard, abilita questo blocco.
    if (ok) {
      // window.location.href = `/?locale=${encodeURIComponent(localeTag)}&cameriere=${encodeURIComponent(cameriere)}`;
    }
  };

  if (loading) {
    return <div className="menu-cameriere-loading">Caricamento…</div>;
  }

  return (
    <MenuCameriereView
      // ✅ sopra: ordini tavolo
      ordiniTavolo={ordiniTavolo}
      tavolo={tavolo}
      cameriere={cameriere}
      numeroCoperti={numeroCoperti}
      // ✅ sotto: menu + carrello nuovo ordine
      categorie={categorie}
      categoriaSelezionata={logic.categoriaSelezionata}
      piattiCategoria={logic.piattiCategoria}
      carrello={logic.carrello}
      showRiepilogo={logic.showRiepilogo}
      setShowRiepilogo={logic.setShowRiepilogo}
      setCategoriaSelezionata={logic.setCategoriaSelezionata}
      aggiungiAlCarrello={logic.aggiungiAlCarrello}
      modificaQuantita={logic.modificaQuantita}
      rimuoviDalCarrello={logic.rimuoviDalCarrello}
      onInviaOrdine={handleInviaOrdine}
    />
  );
}
